import { Fragment } from 'react';
import type { ReactNode, FC, MouseEventHandler } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

import { useViewportType } from '../../utils/useViewportType';
import { config } from '../../../config';
import { PlayButton } from '../PlayButton/PlayButton';
import { FavouritesButton } from '../FavouritesButton/FavouritesButton';
import { useAppSelector } from '../../../modules/hooks';
import { selectFavouritesIds } from '../../../modules/favourites/selectors';
import { useIdentity } from '../../IdentityProvider/IdentityProvider';
import { useServerTime } from '../../utils/useServerTime';
import {
  isCurrentlyLive,
  isDocumentary,
} from '../../../shared/helpers/assetHelpers';
import { useAssetWatchtimeData } from '../../utils/useAssetWatchtimeData';
import { getPath, routeNames } from '../../../shared/helpers/routes';
import { createFavouriteId } from '../../../helpers/favourites';
import { Spacer } from '../../Spacer/Spacer';

import * as styles from './SplashButtons.module.scss';

interface AssetButtonsProps {
  asset?: SvpAssetAdditional;
  trackSplashClick?: (buttonName: string) => void;
  overridePlayButtonClick?: MouseEventHandler;
}

const AssetButtons: FC<AssetButtonsProps> = ({
  asset,
  trackSplashClick,
  overridePlayButtonClick,
}) => {
  let leftButtons: ReactNode[] = [];
  let rightButtons: ReactNode[] = [];
  const { serverTime } = useServerTime();
  const { isLoggedIn } = useIdentity();
  const { isMobile } = useViewportType();
  const { position } = useAssetWatchtimeData(asset?.id, asset?.provider);

  const favouritesIds = useAppSelector(selectFavouritesIds);

  if (!asset) {
    return null;
  }

  const { category } = asset;
  const isLive = isCurrentlyLive(asset, serverTime);

  const isBrandstudio = asset.provider === 'brandstudio';

  const shouldDisplayFavourites =
    config.enableFavourites &&
    !isBrandstudio &&
    !isLive &&
    (category?.isSeries || isDocumentary(asset));

  const isDocumentarySingleAsset = !category?.isSeries && isDocumentary(asset);

  const assetUrl = getPath(routeNames.asset, {
    asset,
    time: position,
  });

  const handleButtonClicked =
    (buttonType: string): MouseEventHandler =>
    (event) => {
      if (overridePlayButtonClick) {
        overridePlayButtonClick(event);
        event.preventDefault();
      }
      if (trackSplashClick) {
        trackSplashClick(buttonType);
      }
    };

  if (isMobile) {
    leftButtons.push(
      <PlayButton
        onClick={handleButtonClicked('PlayButton')}
        assetUrl={assetUrl}
        skipLabel={!isLive}
      />
    );
    if (shouldDisplayFavourites && category) {
      leftButtons.push(
        <FavouritesButton
          item={isDocumentarySingleAsset ? asset : category}
          skipLabel={isMobile}
          onClick={handleButtonClicked('FavouritesButton')}
          isLoggedIn={isLoggedIn}
          checked={favouritesIds.includes(
            isDocumentarySingleAsset
              ? createFavouriteId(asset)
              : createFavouriteId(category)
          )}
        ></FavouritesButton>
      );
    }
  } else {
    leftButtons.push(
      <PlayButton
        assetUrl={assetUrl}
        onClick={handleButtonClicked('PlayButton')}
      />
    );
    if (shouldDisplayFavourites && category) {
      rightButtons.push(
        <FavouritesButton
          item={isDocumentarySingleAsset ? asset : category}
          skipLabel={isMobile}
          isLoggedIn={isLoggedIn}
          onClick={handleButtonClicked('FavouritesButton')}
          checked={favouritesIds.includes(
            isDocumentarySingleAsset
              ? createFavouriteId(asset)
              : createFavouriteId(category)
          )}
        ></FavouritesButton>
      );
    }
  }

  return (
    <>
      <div className={styles.buttonGroup}>
        {leftButtons.map((button, i) => (
          <Fragment key={i}>{button}</Fragment>
        ))}
      </div>
      <Spacer />
      <div className={styles.buttonGroup}>
        {rightButtons.map((button, i) => (
          <Fragment key={i}>{button}</Fragment>
        ))}
      </div>
    </>
  );
};

export { AssetButtons };
